import { useState } from "react";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const links = [
  { label: "The Standard", href: "#about" },
  { label: "Team", href: "#team" },
  { label: "Services", href: "#services" },
  { label: "Portfolio", href: "#portfolio" },
  { label: "Financing", href: "/financing", page: true },
  { label: "Contact", href: "#contact" },
];

export const scrollToSection = (href) => {
  if (window.__lenis) {
    window.__lenis.scrollTo(href, { offset: -72, duration: 1.6 });
  } else {
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  }
};

const Logo = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (
    <a
      href="/"
      data-testid="nav-logo"
      onClick={(e) => {
        e.preventDefault();
        if (pathname !== "/") {
          navigate("/", { state: { scrollTo: "#top" } });
        } else {
          scrollToSection("#top");
        }
      }}
      className="flex items-center gap-3.5 group"
    >
      <img
        src="/images/logo.png"
        alt="Veritas Development Group monogram"
        className="h-11 w-auto transition-transform duration-500 group-hover:scale-105"
        data-testid="nav-logo-image"
      />
      <span className="leading-none">
        <span className="block font-display text-xl sm:text-2xl font-medium tracking-[0.28em] text-[#D4AF37]">
          VERITAS
        </span>
        <span className="block text-[9px] tracking-[0.34em] text-[#A3A3A3] mt-1 whitespace-nowrap">
          DEVELOPMENT GROUP LLC
        </span>
      </span>
    </a>
  );
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const go = (l) => (e) => {
    e.preventDefault();
    setOpen(false);
    if (l.page) {
      if (pathname !== l.href) navigate(l.href);
      return;
    }
    if (pathname !== "/") {
      navigate("/", { state: { scrollTo: l.href } });
    } else {
      scrollToSection(l.href);
    }
  };

  const linkCls = (l) =>
    `text-[10px] tracking-[0.26em] uppercase whitespace-nowrap transition-colors duration-300 ${
      l.page && pathname === l.href ? "text-[#D4AF37]" : "text-[#A3A3A3] hover:text-[#D4AF37]"
    }`;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className="fixed top-0 left-0 right-0 z-50 bg-[#050505] border-b border-[#D4AF37]/20"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7" data-testid="nav-links">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={go(l)}
              className={linkCls(l)}
            >
              {l.label}
            </a>
          ))}
          <a
            href="tel:+18164056181"
            data-testid="nav-phone-button"
            className="flex items-center gap-2 whitespace-nowrap border border-[#D4AF37]/40 text-[#D4AF37] px-5 py-2.5 text-[10px] tracking-[0.22em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors duration-300"
          >
            <Phone size={13} strokeWidth={1.5} />
            816-405-6181
          </a>
        </nav>
        <div className="flex items-center gap-1 lg:hidden">
          <a
            href="tel:+18164056181"
            data-testid="nav-mobile-call-button"
            aria-label="Call Veritas Development Group"
            className="text-[#D4AF37] p-2"
          >
            <Phone size={20} />
          </a>
          <button
            data-testid="nav-mobile-toggle"
            onClick={() => setOpen(!open)}
            className="text-[#D4AF37] p-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-white/10 bg-[#050505]" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={go(l)}
                className={`text-sm tracking-[0.3em] uppercase transition-colors ${
                  l.page && pathname === l.href ? "text-[#D4AF37]" : "text-[#A3A3A3] hover:text-[#D4AF37]"
                }`}
              >
                {l.label}
              </a>
            ))}
            <a
              href="tel:+18164056181"
              data-testid="nav-mobile-phone"
              className="flex items-center gap-2 text-[#D4AF37] text-sm tracking-[0.25em]"
            >
              <Phone size={14} /> 816-405-6181
            </a>
            <a
              href="mailto:info@veritasdevelopmentgroupllc.com"
              data-testid="nav-mobile-email"
              className="flex items-center gap-2 text-[#D4AF37] text-sm tracking-[0.15em] normal-case"
            >
              info@veritasdevelopmentgroupllc.com
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
}
